import { Box, Image, Link, Text, useColorModeValue } from '@chakra-ui/react';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import 'katex/dist/katex.min.css';

import CodeBlock from './CodeBlock';
import { preprocessAnswer } from './preprocess';

interface MarkdownRendererProps {
  content: string;
  /** Smaller type scale (used inside the streaming typewriter / compact contexts). */
  compact?: boolean;
}

// drop the `node` prop react-markdown injects (not a valid DOM/Chakra attr)
const clean = (props: any) => {
  const { node, ...rest } = props;
  return rest;
};

/**
 * Standard markdown renderer for chat/summary answers — react-markdown + GFM + math (KaTeX),
 * with design-system styled element overrides (headings, lists, code, tables, blockquote, links).
 * Replaces the old hand-rolled `formatAnswer` line-parser. Theme-aware via DS tokens.
 */
const MarkdownRenderer = ({ content }: MarkdownRendererProps) => {
  const inlineCodeBg = useColorModeValue('#ececfb', '#221f3a');
  const inlineCodeColor = useColorModeValue('#5b3ea8', '#cbb8ff');
  const tableBorder = useColorModeValue('#d0d4da', '#272b33');
  const theadBg = useColorModeValue('#f6f7f9', '#1d2027');

  const components = {
    h1: (p: any) => (
      <Text
        {...clean(p)}
        as='h1'
        mt={6}
        mb={3}
        fontSize='1.5em'
        fontWeight={700}
        lineHeight={1.3}
      />
    ),
    h2: (p: any) => (
      <Text
        {...clean(p)}
        as='h2'
        mt={5}
        mb={2.5}
        fontSize='1.3em'
        fontWeight={700}
        lineHeight={1.3}
      />
    ),
    h3: (p: any) => (
      <Text
        {...clean(p)}
        as='h3'
        mt={4}
        mb={2}
        fontSize='1.15em'
        fontWeight={600}
        lineHeight={1.35}
      />
    ),
    h4: (p: any) => <Text {...clean(p)} as='h4' mt={3} mb={1.5} fontSize='1em' fontWeight={600} />,
    h5: (p: any) => <Text {...clean(p)} as='h5' mt={3} mb={1} fontSize='0.95em' fontWeight={600} />,
    h6: (p: any) => (
      <Text
        {...clean(p)}
        as='h6'
        mt={3}
        mb={1}
        color='text.muted'
        fontSize='0.9em'
        fontWeight={600}
      />
    ),
    p: (p: any) => <Text {...clean(p)} mb={3} lineHeight={1.7} />,
    a: (p: any) => (
      <Link
        {...clean(p)}
        color='primary.solid'
        textDecoration='underline'
        _hover={{ color: 'primary.hover' }}
        isExternal
      />
    ),
    ul: (p: any) => (
      <Box
        {...clean(p)}
        as='ul'
        sx={{ listStyleType: 'disc', listStylePosition: 'outside' }}
        my={2}
        pl={5}
      />
    ),
    ol: (p: any) => (
      <Box
        {...clean(p)}
        as='ol'
        sx={{ listStyleType: 'decimal', listStylePosition: 'outside' }}
        my={2}
        pl={5}
      />
    ),
    // react-markdown wraps loose-list items in <p> (block), which dropped the text below the
    // bullet ("orphan bullet"). Render the item's first paragraph inline so the bullet + text
    // share a line, and tighten paragraph spacing. (#78687)
    li: (p: any) => (
      <Box
        {...clean(p)}
        as='li'
        sx={{
          display: 'list-item',
          '& > p': { mb: 1 },
          '& > p:first-of-type': { display: 'inline' },
          '& > p:last-of-type': { mb: 0 },
        }}
        mb={1}
        lineHeight={1.65}
      />
    ),
    blockquote: (p: any) => (
      <Box
        {...clean(p)}
        as='blockquote'
        my={3}
        py={1}
        pl={4}
        color='text.muted'
        fontStyle='italic'
        borderLeftWidth='3px'
        borderLeftColor='primary.solid'
      />
    ),
    hr: (p: any) => (
      <Box {...clean(p)} as='hr' my={4} borderColor='border.default' borderTopWidth='1px' />
    ),
    img: (p: any) => <Image {...clean(p)} maxW='100%' my={2} borderRadius='md' />,
    table: (p: any) => (
      <Box overflowX='auto' my={3}>
        <Box
          {...clean(p)}
          as='table'
          sx={{ borderCollapse: 'collapse', fontSize: '0.95em' }}
          w='100%'
        />
      </Box>
    ),
    thead: (p: any) => <Box {...clean(p)} as='thead' bg={theadBg} />,
    th: (p: any) => (
      <Box
        {...clean(p)}
        as='th'
        px={3}
        py={2}
        fontWeight={600}
        textAlign='left'
        borderWidth='1px'
        borderColor={tableBorder}
      />
    ),
    td: (p: any) => (
      <Box {...clean(p)} as='td' px={3} py={2} borderWidth='1px' borderColor={tableBorder} />
    ),
    code: (p: any) => {
      const { inline, className, children } = p;
      const match = /language-(\w+)/.exec(className || '');
      const text = String(children ?? '').replace(/\n$/, '');
      const isBlock = !inline && (match || text.includes('\n'));
      if (isBlock) {
        return <CodeBlock code={text} language={match?.[1]} />;
      }
      return (
        <Box
          as='code'
          sx={{ wordBreak: 'break-word' }}
          mx='1px'
          px='0.4em'
          py='0.1em'
          color={inlineCodeColor}
          fontFamily='"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'
          fontSize='0.88em'
          bg={inlineCodeBg}
          borderRadius='4px'
        >
          {children}
        </Box>
      );
    },
    pre: (p: any) => <>{p.children}</>, // CodeBlock handles its own <pre>
  };

  return (
    <Box
      sx={{
        wordBreak: 'break-word',
        // The chat/summary answer wrapper sets white-space:pre-line (for plain-text streaming),
        // which is INHERITED here and turns react-markdown's source newlines between <li>/<p>
        // into real line breaks → orphan bullets + huge gaps. Markdown manages its own
        // whitespace, so force it back to normal for the rendered tree. (#78687)
        whiteSpace: 'normal',
        '& > :first-of-type': { mt: 0 },
        '& > :last-child': { mb: 0 },
        '& .katex-display': { overflowX: 'auto', overflowY: 'hidden', py: 1 },
      }}
      color='text.default'
      fontSize='base'
      lineHeight={1.7}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex]}
        components={components}
      >
        {preprocessAnswer(content)}
      </ReactMarkdown>
    </Box>
  );
};

export default MarkdownRenderer;
